import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { PageHeader, Empty } from "@/components/Bits";
import StatusChip from "@/components/StatusChip";
import Modal from "@/components/Modal";

const BLANK = { name: "", phone: "", email: "", address: "", tax_id: "" };

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [params] = useSearchParams();
  const nav = useNavigate();

  const load = async () => setList((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = params.get("id");
    if (id) api.get(`/customers/${id}`).then((r) => setDetail(r.data));
  }, [params]);

  const save = async () => {
    try {
      if (form.id) await api.put(`/customers/${form.id}`, form);
      else await api.post("/customers", form);
      toast.success("Customer saved");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (c) => setDetail((await api.get(`/customers/${c.id}`)).data);

  const filtered = list.filter((c) => !q || (c.name + c.phone + c.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader eyebrow="Directory" title="Customers" subtitle="Everyone who shops the counter or online — with balances and history.">
        <button className="btn btn-primary" onClick={() => setForm({ ...BLANK })} data-testid="new-customer-btn"><Plus size={16} /> New customer</button>
      </PageHeader>

      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C929B]" />
        <input className="field pl-9" placeholder="Search name, phone, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" />
      </div>

      <div className="card overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th className="num">Total spent</th><th className="num">Outstanding</th><th></th></tr></thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={() => openDetail(c)} data-testid={`customer-row-${c.id}`}>
                <td className="font-medium">{c.name}</td>
                <td className="font-num text-[#5C626A]">{c.phone}</td>
                <td className="text-[#5C626A]">{c.email || "—"}</td>
                <td className="num font-num">{inr(c.total_spent)}</td>
                <td className="num font-num" style={{ color: c.outstanding > 0 ? "#A3411D" : "#8C929B" }}>{inr(c.outstanding)}</td>
                <td className="num"><button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setForm(c); }}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <Empty>No customers found</Empty>}
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Edit Customer" : "New Customer"} testid="customer-form">
        {form && (
          <div className="space-y-4">
            {["name", "phone", "email", "address", "tax_id"].map((f) => (
              <div key={f}>
                <label className="label">{f === "tax_id" ? "Tax ID / GSTIN" : f}</label>
                <input className="field" value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`cust-${f}`} />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={!form.name} data-testid="save-customer">Save</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name} wide testid="customer-detail">
        {detail && (
          <div>
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div><div className="eyebrow mb-1">Contact</div>{detail.phone}<br />{detail.email || "—"}</div>
              <div><div className="eyebrow mb-1">Address</div>{detail.address || "—"}</div>
              <div><div className="eyebrow mb-1">Total spent</div><span className="font-num">{inr(detail.total_spent)}</span></div>
              <div><div className="eyebrow mb-1">Outstanding</div><span className="font-num" style={{ color: detail.outstanding > 0 ? "#A3411D" : "#2D5A3C" }}>{inr(detail.outstanding)}</span></div>
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow">Invoice history</div>
              <button className="btn btn-primary btn-sm" onClick={() => nav(`/sales/new?customer=${detail.id}`)} data-testid="customer-new-invoice"><Plus size={14} /> New invoice</button>
            </div>
            <div className="border border-[#E6E1D8] rounded-lg overflow-auto max-h-80">
              <table className="tbl">
                <thead><tr><th>Invoice</th><th>Date</th><th>Channel</th><th className="num">Total</th><th className="num">Balance</th><th>Status</th></tr></thead>
                <tbody>
                  {detail.invoices.map((i) => (
                    <tr key={i.id} className="cursor-pointer" onClick={() => nav(`/invoices/${i.id}`)}>
                      <td className="font-num">{i.invoice_number}</td><td>{fmtDate(i.date)}</td><td className="text-[#5C626A]">{i.channel}</td>
                      <td className="num font-num">{inr(i.total)}</td><td className="num font-num">{inr(i.balance)}</td><td><StatusChip status={i.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {detail.invoices.length === 0 && <Empty>No invoices yet</Empty>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
